import { type Key, type LayoutInfo, type Rect, type TableLayoutProps } from 'react-aria-components';
import { TableLayout } from '@launchpad-ui/components';

export type DataTableScrollAnchorTarget = {
  getViewportTop: () => number | undefined;
  scrollBy: (delta: number) => void;
};

export type DataTableLayoutOptions = TableLayoutProps & {
  additionalResultsHeight?: number;
  renderedHeaderHeight?: number;
  stickyColumnCount?: number;
  rendersAllColumns?: boolean;
  stickyHeaderOffset?: number;
  scrollAnchorContent?: unknown;
  scrollAnchorTarget?: DataTableScrollAnchorTarget;
};

type InvalidationContext = Parameters<TableLayout<object, DataTableLayoutOptions>['update']>[0];

type ScrollAnchor = { key: Key; y: number };

const MIN_ANCHOR_VISIBLE_HEIGHT = 4;

const STICKY_COLUMN_ANCESTOR_TYPES = new Set(['header', 'headerrow', 'rowgroup', 'row']);

export class DataTableLayout<T extends object = object> extends TableLayout<T, DataTableLayoutOptions> {
  private additionalResultsHeight = 0;
  private renderedHeaderHeight = 0;
  private stickyColumnCount = 0;
  private rendersAllColumns = false;
  private stickyHeaderOffset = 0;
  private offsetHeaders = new WeakMap<LayoutInfo, LayoutInfo>();
  private scrollAnchorTarget: DataTableScrollAnchorTarget | undefined;
  private scrollAnchorContent: unknown;
  protected pendingScrollShift = 0;
  private isScrollShiftQueued = false;

  shouldInvalidateLayoutOptions(newOptions: DataTableLayoutOptions, oldOptions: DataTableLayoutOptions) {
    if (newOptions === undefined || newOptions === null || oldOptions === undefined || oldOptions === null) {
      return newOptions !== oldOptions;
    }
    return (
      super.shouldInvalidateLayoutOptions(newOptions, oldOptions) ||
      newOptions.additionalResultsHeight !== oldOptions.additionalResultsHeight ||
      newOptions.renderedHeaderHeight !== oldOptions.renderedHeaderHeight ||
      newOptions.stickyColumnCount !== oldOptions.stickyColumnCount ||
      newOptions.rendersAllColumns !== oldOptions.rendersAllColumns ||
      newOptions.stickyHeaderOffset !== oldOptions.stickyHeaderOffset
    );
  }

  update(invalidationContext: InvalidationContext) {
    this.additionalResultsHeight = invalidationContext.layoutOptions?.additionalResultsHeight ?? 0;
    this.renderedHeaderHeight = invalidationContext.layoutOptions?.renderedHeaderHeight ?? 0;
    this.stickyColumnCount = invalidationContext.layoutOptions?.stickyColumnCount ?? 0;
    this.rendersAllColumns = invalidationContext.layoutOptions?.rendersAllColumns ?? false;
    const stickyHeaderOffset = invalidationContext.layoutOptions?.stickyHeaderOffset ?? 0;
    if (stickyHeaderOffset !== this.stickyHeaderOffset) {
      this.stickyHeaderOffset = stickyHeaderOffset;
      this.offsetHeaders = new WeakMap();
    }
    this.scrollAnchorTarget = invalidationContext.layoutOptions?.scrollAnchorTarget;
    const scrollAnchorContent = invalidationContext.layoutOptions?.scrollAnchorContent;
    const anchors = scrollAnchorContent === this.scrollAnchorContent ? this.captureScrollAnchors() : [];
    if (scrollAnchorContent !== this.scrollAnchorContent) {
      this.pendingScrollShift = 0;
    }
    this.scrollAnchorContent = scrollAnchorContent;
    const heightOnlyChange =
      invalidationContext.sizeChanged === true &&
      invalidationContext.heightChanged === true &&
      invalidationContext.widthChanged !== true;
    super.update(heightOnlyChange ? { ...invalidationContext, sizeChanged: false } : invalidationContext);
    for (const anchor of anchors) {
      const y = this.getLayoutInfo(anchor.key)?.rect.y;
      if (y === undefined) {
        continue;
      }
      if (y !== anchor.y) {
        this.pendingScrollShift += y - anchor.y;
        this.queueScrollShift();
      }
      break;
    }
  }

  protected queueScrollShift() {
    if (this.isScrollShiftQueued) {
      return;
    }
    this.isScrollShiftQueued = true;
    queueMicrotask(() => {
      this.isScrollShiftQueued = false;
      this.flushScrollShift();
    });
  }

  private captureScrollAnchors(): ScrollAnchor[] {
    const viewportTop = this.scrollAnchorTarget?.getViewportTop();
    if (viewportTop === undefined) {
      return [];
    }
    const header = this.rootNodes.find((node) => node.layoutInfo.type === 'header');
    const body = this.rootNodes.find((node) => node.layoutInfo.type === 'rowgroup');
    const rows = body?.children;
    const bodyTop = viewportTop + (header?.layoutInfo.rect.height ?? 0);
    if (!body || !rows || rows.length === 0 || bodyTop <= body.layoutInfo.rect.y) {
      return [];
    }
    const top = bodyTop + MIN_ANCHOR_VISIBLE_HEIGHT;
    let low = 0;
    let high = rows.length - 1;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (rows[middle].layoutInfo.rect.maxY > top) {
        high = middle;
      } else {
        low = middle + 1;
      }
    }
    const first = rows[low].layoutInfo;
    if (first.type !== 'row' || first.rect.maxY <= top) {
      return [];
    }
    const bottom = viewportTop + (this.virtualizer?.visibleRect.height ?? 0);
    const anchors: ScrollAnchor[] = [];
    for (let index = low; index < rows.length; index += 1) {
      const row = rows[index].layoutInfo;
      if (row.type === 'row') {
        anchors.push({ key: row.key, y: row.rect.y });
      }
      if (row.rect.y >= bottom) {
        break;
      }
    }
    return anchors;
  }

  private flushScrollShift() {
    const shift = this.pendingScrollShift;
    this.pendingScrollShift = 0;
    if (shift !== 0) {
      this.scrollAnchorTarget?.scrollBy(shift);
    }
  }

  getVisibleLayoutInfos(rect: Rect) {
    let visibleRect = rect;
    if (this.rendersAllColumns) {
      visibleRect = rect.copy();
      visibleRect.x = 0;
      visibleRect.width = Math.max(rect.maxX, this.getContentSize().width);
    }
    const layoutInfos = super.getVisibleLayoutInfos(visibleRect);
    if (this.stickyColumnCount > 0) {
      for (const layoutInfo of layoutInfos) {
        if (STICKY_COLUMN_ANCESTOR_TYPES.has(layoutInfo.type)) {
          layoutInfo.allowOverflow = true;
        }
      }
    }
    if (this.stickyHeaderOffset === 0) {
      return layoutInfos;
    }
    return layoutInfos.map((layoutInfo) => (layoutInfo.type === 'header' ? this.offsetHeader(layoutInfo) : layoutInfo));
  }

  private offsetHeader(header: LayoutInfo) {
    let offsetHeader = this.offsetHeaders.get(header);
    if (!offsetHeader) {
      offsetHeader = header.copy();
      offsetHeader.rect.y += this.stickyHeaderOffset;
      this.offsetHeaders.set(header, offsetHeader);
    }
    offsetHeader.allowOverflow = header.allowOverflow;
    return offsetHeader;
  }

  protected isStickyColumn(node: { index: number; colIndex?: number | null }) {
    return (node.colIndex ?? node.index) < this.stickyColumnCount;
  }

  protected buildCell(...args: Parameters<TableLayout<T, DataTableLayoutOptions>['buildCell']>) {
    const [node] = args;
    const isNewCell = !this.layoutNodes.has(node.key);
    const cell = super.buildCell(...args);
    const rowHeight =
      node.parentKey === null || node.parentKey === undefined
        ? undefined
        : this.layoutNodes.get(node.parentKey)?.layoutInfo.rect.height;
    if (isNewCell && cell.layoutInfo.estimatedSize && rowHeight !== undefined && rowHeight > 0) {
      cell.layoutInfo.rect.height = rowHeight;
    }
    return cell;
  }

  protected buildTableHeader() {
    const header = super.buildTableHeader();
    if (this.renderedHeaderHeight > header.layoutInfo.rect.height) {
      header.layoutInfo.rect.height = this.renderedHeaderHeight;
    }
    if (this.additionalResultsHeight > 0) {
      header.layoutInfo.rect.height += this.additionalResultsHeight;
    }
    return header;
  }
}
